<template>
  <SplitPanelShell
    class="draft-bills-captured"
    title="Captured"
    icon="bi bi-link-45deg"
    :toolbar-columns="1"
    layout="single"
    :count="visibleGroups.length || null"
    :status-message="statusMessage"
    :status-type="statusType"
  >
    <template #actions>
      <SecondaryButton
        icon="bi bi-arrow-clockwise"
        label=""
        custom-class="split-panel-shell__icon-action"
        :loading="loading"
        :disabled="loading"
        title="Refresh matches"
        @click="refreshAll"
      />
    </template>

    <template #content>
      <div class="draft-bills-captured__body">
      <div v-if="loading && !matchResult.groups.length" class="split-panel-shell__empty">
        <i class="bi bi-arrow-repeat spinning" aria-hidden="true"></i>
        <p>Matching draft bills with captured mail...</p>
      </div>

      <div v-else-if="!mailConnected" class="split-panel-shell__empty">
        <div class="split-panel-shell__empty-icon">
          <i class="bi bi-envelope-x" aria-hidden="true"></i>
        </div>
        <h4>Connect mail first</h4>
        <p>Open Inbox, connect Outlook or Gmail, then return here to review matches.</p>
        <SecondaryButton
          icon="bi bi-inbox"
          label="Open Inbox"
          compact
          @click="$emit('open-inbox')"
        />
      </div>

      <div v-else-if="!hasMailSources" class="split-panel-shell__empty">
        <div class="split-panel-shell__empty-icon">
          <i class="bi bi-envelope-check" aria-hidden="true"></i>
        </div>
        <h4>Set up bill mail senders</h4>
        <p>Add vendor From senders in Bill Mail Setup so mail can be captured.</p>
        <SecondaryButton
          icon="bi bi-gear"
          label="Bill Mail Setup"
          compact
          @click="$emit('configure-mail')"
        />
      </div>

      <div v-else-if="!drafts.length" class="split-panel-shell__empty">
        <div class="split-panel-shell__empty-icon">
          <i class="bi bi-file-earmark-plus" aria-hidden="true"></i>
        </div>
        <h4>No draft bills</h4>
        <p>Create draft bills first, then Captured will group them with matching mail.</p>
        <SecondaryButton
          icon="bi bi-file-earmark-text"
          label="Open Draft Bills"
          compact
          @click="$emit('open-drafts')"
        />
      </div>

      <div v-else-if="!emails.length" class="split-panel-shell__empty">
        <div class="split-panel-shell__empty-icon">
          <i class="bi bi-inbox" aria-hidden="true"></i>
        </div>
        <h4>No captured mail yet</h4>
        <p>Load the Inbox from your configured senders, then refresh Captured.</p>
        <div class="draft-bills-captured__empty-actions">
          <SecondaryButton
            icon="bi bi-inbox"
            label="Open Inbox"
            compact
            @click="$emit('open-inbox')"
          />
          <SecondaryButton
            icon="bi bi-arrow-clockwise"
            label="Refresh"
            compact
            :loading="loading"
            @click="refreshAll"
          />
        </div>
      </div>

      <div v-else-if="!hasSelectedBill" class="split-panel-shell__empty">
        <div class="split-panel-shell__empty-icon">
          <i class="bi bi-cursor" aria-hidden="true"></i>
        </div>
        <h4>Select bills</h4>
        <p>Check the draft bills that belong to one captured mail.</p>
      </div>

      <div v-else-if="!selectedGroup" class="split-panel-shell__empty">
        <div class="split-panel-shell__empty-icon">
          <i class="bi bi-envelope-x" aria-hidden="true"></i>
        </div>
        <h4>{{ dismissedPairs.length ? 'No more mail for this bill' : 'No captured mail for this bill' }}</h4>
        <p>
          {{
            dismissedPairs.length
              ? 'Every loaded message was skipped for this draft. Restore them to keep reviewing until the right mail matches.'
              : 'This draft is not paired with a related invoice mail yet. Choose another captured bill on the left.'
          }}
        </p>
        <SecondaryButton
          v-if="dismissedPairs.length"
          icon="bi bi-arrow-counterclockwise"
          label="Restore hidden matches"
          compact
          @click="restoreHiddenMatches"
        />
      </div>

      <div v-else-if="selectedGroup" class="draft-bills-captured__reading">
        <header
          v-if="selectedRelatedMails.length"
          class="captured-group__related captured-group__related--top"
        >
          <span class="captured-group__related-label">
            Related mail
            <span class="captured-group__related-count">{{ selectedRelatedMails.length }}</span>
          </span>
          <div
            ref="relatedMailList"
            class="captured-group__related-list"
          >
            <button
              v-for="row in visibleRelatedMails"
              :key="row.email.id"
              type="button"
              class="captured-group__related-chip"
              :class="{ 'captured-group__related-chip--active': isRelatedMailSelected(row) }"
              :title="row.email.subject || 'Open this related mail'"
              @click="selectRelatedMail(selectedGroup, row)"
            >
              <i class="bi bi-envelope" aria-hidden="true"></i>
              <span>{{ relatedMailLabel(row.email) }}</span>
            </button>
          </div>
          <div v-if="showRelatedMailNav" class="captured-group__related-stepper">
            <button
              type="button"
              class="captured-group__related-arrow"
              :disabled="!canGoRelatedPrev"
              title="Previous mail"
              aria-label="Previous related mail"
              @click="stepRelatedMail(-1)"
            >
              <i class="bi bi-chevron-left" aria-hidden="true"></i>
            </button>
            <span class="captured-group__related-pos">
              {{ selectedRelatedMailIndex + 1 }} of {{ selectedRelatedMails.length }}
            </span>
            <button
              type="button"
              class="captured-group__related-arrow"
              :disabled="!canGoRelatedNext"
              title="Next mail"
              aria-label="Next related mail"
              @click="stepRelatedMail(1)"
            >
              <i class="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        </header>
        <div class="split-panel-shell__list-scroll draft-bills-captured__scroll">
        <div class="captured-reading-cmd">
          <div class="captured-reading-cmd__identity">
            <span class="captured-reading-cmd__from">
              {{ selectedGroup.bill?.vendorName || 'Draft bill' }}
              <template v-if="relatedDraftBills.length > 1"> · {{ relatedDraftBills.length }} bills</template>
            </span>
            <span class="captured-reading-cmd__subject">{{ relatedMailLabel(selectedGroup.matches?.[0]?.email) }}</span>
          </div>
          <div class="captured-reading-cmd__actions">
            <PrimaryButton
              icon="bi bi-pencil-square"
              label="Review & adjust"
              compact
              :disabled="!canCreateBill"
              title="Open this matched draft bill and adjust it from the captured mail"
              @click="goToReviewDraft(selectedGroup)"
            />
            <SecondaryButton
              v-show="selectedMailHasAttachments"
              icon="bi bi-arrow-clockwise"
              :label="selectedSummary?.loading ? 'Extracting…' : (selectedSummary?.summary ? 'Refresh' : 'Extract')"
              :loading="!!selectedSummary?.loading"
              :disabled="!!selectedSummary?.loading"
              @click="extractAttachmentSummary(selectedGroup, { force: true })"
            />
            <SecondaryButton
              icon="bi bi-x-lg"
              label="Not this mail"
              title="Skip this mail and show the next captured message for this bill"
              @click="clearCapturedMatch(selectedGroup)"
            />
          </div>
        </div>
        <article
          class="captured-group"
          :class="`captured-group--${selectedGroup.confidence}`"
        >
          <div class="captured-group__pair">
            <article class="dbc-card captured-bill-card captured-bill-card--draft">
              <div class="captured-bill-card__ribbon">
                <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
                {{ relatedDraftBills.length > 1 ? 'Draft bills' : 'Draft bill' }}
              </div>

              <dl class="captured-main">
                <div
                  v-for="row in selectedDraftRows"
                  :key="`draft-${row.key}`"
                  class="captured-main__row"
                  :class="{
                    'captured-main__row--total': row.key === 'total',
                    'captured-main__row--match': row.matchStatus === 'match',
                  }"
                >
                  <dt>{{ row.label }}</dt>
                  <dd>
                    <span class="captured-main__colon" aria-hidden="true">:</span>
                    <span class="captured-main__value">{{ row.value }}</span>
                    <i
                      v-if="row.matchStatus === 'match'"
                      class="bi bi-check-circle-fill captured-group__tick"
                      title="Match"
                      aria-label="Match"
                    ></i>
                    <span
                      v-else-if="row.matchStatus === 'mismatch'"
                      class="captured-group__inline-tag captured-group__inline-tag--mismatch"
                    >Differ</span>
                  </dd>
                </div>
              </dl>
            </article>

            <div class="captured-group__link" aria-hidden="true">
              <span class="captured-group__link-pill">
                <i class="bi bi-arrow-left-right"></i>
              </span>
            </div>

            <article class="dbc-card captured-bill-card captured-bill-card--mail">
              <div class="captured-bill-card__ribbon captured-bill-card__ribbon--mail">
                <i class="bi bi-envelope-open" aria-hidden="true"></i>
                Captured mail
              </div>

              <p v-if="selectedSummary?.loading" class="captured-group__qwen-status">
                Reading attachment…
              </p>
              <p v-else-if="selectedSummary?.error" class="captured-group__qwen-error">
                {{ selectedSummary.error }}
              </p>
              <dl
                v-if="selectedMailRows.length"
                class="captured-main"
              >
                <div
                  v-for="row in selectedMailRows"
                  :key="`extract-${row.key}`"
                  class="captured-main__row"
                  :class="{
                    'captured-main__row--total': row.emphasize || row.key === 'total',
                    'captured-main__row--match': row.matchStatus === 'match',
                  }"
                >
                  <dt>{{ row.label }}</dt>
                  <dd>
                    <span class="captured-main__colon" aria-hidden="true">:</span>
                    <span class="captured-main__value">{{ row.value }}</span>
                    <i
                      v-if="row.matchStatus === 'match'"
                      class="bi bi-check-circle-fill captured-group__tick"
                      title="Match"
                      aria-label="Match"
                    ></i>
                    <span
                      v-else-if="row.matchStatus === 'mismatch'"
                      class="captured-group__inline-tag captured-group__inline-tag--mismatch"
                    >Differ</span>
                  </dd>
                </div>
              </dl>
              <p
                v-else-if="!selectedSummary?.loading && !selectedSummary?.error"
                class="dbc-card__empty"
              >
                Extract attachment to review bill details.
              </p>

              <div
                v-if="selectedSourcePanels.length"
                class="captured-source"
              >
                <section
                  v-for="panel in selectedSourcePanels"
                  :key="panel.key"
                  class="captured-source__card"
                >
                  <header class="captured-source__head">
                    <span class="captured-source__title">{{ panel.title }}</span>
                  </header>
                  <p v-if="panel.error" class="captured-source__error">{{ panel.error }}</p>
                  <div v-if="panel.blocks.length" class="captured-source__body">
                    <template v-for="(block, bi) in panel.blocks">
                      <h4
                        v-if="block.type === 'heading'"
                        :key="`${panel.key}-h-${bi}`"
                        class="captured-source__h"
                      >{{ block.text }}</h4>
                      <p
                        v-else-if="block.type === 'p'"
                        :key="`${panel.key}-p-${bi}`"
                        class="captured-source__p"
                      >{{ block.text }}</p>
                      <dl
                        v-else-if="block.type === 'fields'"
                        :key="`${panel.key}-f-${bi}`"
                        class="captured-source__fields"
                      >
                        <div
                          v-for="(row, ri) in block.rows"
                          :key="`${panel.key}-f-${bi}-${ri}`"
                          class="captured-source__field"
                        >
                          <dt>{{ row.label }}</dt>
                          <dd>{{ row.value }}</dd>
                        </div>
                      </dl>
                    </template>
                  </div>
                </section>
              </div>

              <div
                v-if="selectedAttachments.length"
                class="mail-attachments captured-mail-attachments"
              >
                <div class="mail-attachments__title">
                  <i class="bi bi-paperclip" aria-hidden="true"></i>
                  <span>
                    {{ selectedAttachments.length }}
                    attachment{{ selectedAttachments.length > 1 ? 's' : '' }}
                  </span>
                  <span
                    v-if="selectedSummary?.billAttachmentCount"
                    class="captured-mail-attachments__saved"
                  >
                    Saved on draft bill
                  </span>
                </div>
                <ul class="mail-attachments__grid">
                  <li
                    v-for="attachment in selectedAttachments"
                    :key="attachment.id || attachment.name"
                    class="mail-attachments__item"
                  >
                    <button
                      type="button"
                      class="mail-attachment-card"
                      :class="attachmentCardClass(attachment.name)"
                      :disabled="activeAttachmentId === attachment.id"
                      :title="`Open ${attachment.name || 'attachment'} — also saved on this draft bill`"
                      @click="openCapturedAttachment(selectedGroup, attachment)"
                    >
                      <span class="mail-attachment-card__icon" aria-hidden="true">
                        <i :class="attachmentFileIcon(attachment.name)"></i>
                      </span>
                      <span class="mail-attachment-card__meta">
                        <span class="mail-attachment-card__name">{{ attachment.name }}</span>
                        <span class="mail-attachment-card__size">{{ formatFileSize(attachment.size) }}</span>
                      </span>
                      <i
                        v-if="activeAttachmentId === attachment.id"
                        class="bi bi-arrow-repeat spinning mail-attachment-card__spinner"
                        aria-hidden="true"
                      ></i>
                      <i v-else class="bi bi-box-arrow-up-right mail-attachment-card__download" aria-hidden="true"></i>
                    </button>
                  </li>
                </ul>
              </div>
            </article>
          </div>
        </article>
        </div>
      </div>
      </div>
    </template>
  </SplitPanelShell>
</template>

<script>
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SplitPanelShell from '@/modules/vendor/components/SplitPanelShell.vue';
import { hasConfiguredMailSources, parseAddressList } from '@/modules/vendor/utils/mailInboxFetchRules';
import {
  mailSenderEmail,
  matchDraftBillsToMail,
  capturePairKey,
} from '@/modules/vendor/utils/matchDraftBillsToMail';
import {
  ensureRecordDetails,
  getBillDisplayLines,
} from '@/modules/vendor/utils/billsExpensesDetailService';
import {
  parseBillAttachmentSummary,
  toBillDetailsCard,
  extractSummaryTotal,
  compareDraftAndExtractedTotal,
  capturedSourceFromSummary,
  toReadableSourceBlocks,
} from '@/modules/vendor/utils/formatBillAttachmentSummary';
import { formatUsDate, formatUsPeriodText } from '@/modules/vendor/utils/splitPanelUtils';

const RELATED_MAIL_PAGE_SIZE = 10;

export default {
  name: 'DraftBillsCapturedPanel',
  components: {
    SecondaryButton,
    PrimaryButton,
    SplitPanelShell,
  },
  props: {
    emails: {
      type: Array,
      default: () => [],
    },
    mailConnected: {
      type: Boolean,
      default: false,
    },
    fetchRules: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
    selectedBillId: {
      default: null,
    },
    selectedBillIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['configure-mail', 'open-inbox', 'open-drafts', 'refresh-mail', 'matches-updated', 'select-bill'],
  data() {
    return {
      drafts: [],
      vendors: [],
      paymentTerms: [],
      loading: false,
      statusMessage: '',
      statusType: '',
      /** @type {Record<string, { loading?: boolean, summary?: string, error?: string, provider?: string, model?: string, extracts?: Array, providerLabel?: string, attachments?: Array, billAttachmentCount?: number }>} */
      attachmentSummaries: {},
      reviewPolls: {},
      /** @type {Record<string, Array>} */
      groupAttachments: {},
      activeAttachmentId: null,
      dismissedPairs: [],
      preferredMailByBillId: {},
    };
  },
  computed: {
    companyId() {
      return this.$route?.params?.id || this.$route?.params?.companyId || null;
    },
    hasMailSources() {
      return hasConfiguredMailSources(this.fetchRules);
    },
    vendorEmailById() {
      const map = Object.create(null);
      (this.vendors || []).forEach((vendor) => {
        if (vendor?.id == null) return;
        const email = String(vendor.email || '').trim();
        if (email) map[String(vendor.id)] = email;
      });
      return map;
    },
    extractedAmountByMailId() {
      const map = Object.create(null);
      Object.values(this.attachmentSummaries || {}).forEach((state) => {
        if (state?.mailId == null || state.extractedAmount == null) return;
        map[String(state.mailId)] = state.extractedAmount;
      });
      return map;
    },
    matchResult() {
      return matchDraftBillsToMail(this.drafts, this.emails, {
        vendorEmailById: this.vendorEmailById,
        minScore: 40,
        dismissedPairs: this.dismissedPairs,
        preferredMailByBillId: this.preferredMailByBillId,
        extractedAmountByMailId: this.extractedAmountByMailId,
        configuredSenders: parseAddressList(this.fetchRules?.fromAddresses),
      });
    },
    hasSelectedBill() {
      return this.selectedBillId != null && this.selectedBillId !== '';
    },
    selectedBills() {
      const ids = (this.selectedBillIds || []).map((id) => String(id)).filter(Boolean);
      if (!ids.length && this.hasSelectedBill) ids.push(String(this.selectedBillId));
      const byId = new Map((this.drafts || []).filter((bill) => bill?.id != null).map((bill) => [String(bill.id), bill]));
      return ids.map((id) => byId.get(id) || { id, vendorName: 'Bill', no: '' });
    },
    relatedDraftBills() {
      const bills = (this.selectedBills || []).filter((bill) => bill?.id != null);
      if (bills.length) return bills;
      const bill = this.selectedGroup?.bill;
      return bill ? [bill] : [];
    },
    combinedSelectedAmount() {
      return this.relatedDraftBills.reduce(
        (sum, bill) => sum + (Number(bill.originalAmount) || 0),
        0
      );
    },
    selectedDraft() {
      if (!this.hasSelectedBill) return null;
      return (this.drafts || []).find((bill) => String(bill.id) === String(this.selectedBillId)) || null;
    },
    remainingMailsForSelectedBill() {
      const bill = this.selectedDraft;
      if (!bill) return [];
      const billId = String(bill.id);
      return (this.emails || []).filter((email) => {
        if (!email?.id) return false;
        return !this.dismissedPairs.includes(capturePairKey(billId, email.id));
      });
    },
    visibleGroups() {
      const groups = this.matchResult?.groups || [];
      if (!this.hasSelectedBill) return [];
      return groups.filter((group) => String(group?.bill?.id) === String(this.selectedBillId));
    },
    selectedGroup() {
      const remaining = this.remainingMailsForSelectedBill;
      const bill = this.selectedDraft;
      if (!bill || !remaining.length) return null;
      const preferredId = String(
        this.preferredMailByBillId[String(bill.id)] ?? this.preferredMailByBillId[bill.id] ?? ''
      );
      const matched = this.visibleGroups[0] || null;
      const fallbackId = String(matched?.matches?.[0]?.email?.id || remaining[0].id);
      const primary =
        remaining.find((email) => String(email.id) === preferredId) ||
        remaining.find((email) => String(email.id) === fallbackId) ||
        remaining[0];
      const rest = remaining.filter((email) => String(email.id) !== String(primary.id));
      const scoredRows = new Map(
        (matched?.matches || []).map((row) => [String(row?.email?.id || ''), row])
      );
      const toRow = (email) =>
        scoredRows.get(String(email.id)) || {
          email,
          score: 0,
          reasons: ['Captured mail'],
          amountMatched: false,
        };
      return {
        id: matched?.id || `capture-${bill.id}`,
        bill,
        vendorEmail:
          matched?.vendorEmail ||
          this.vendorEmailById[String(bill.vendorId)] ||
          bill.vendorEmail ||
          '',
        matches: [toRow(primary), ...rest.map(toRow)],
        bestScore: matched?.bestScore || toRow(primary).score || 0,
        amountMatched: !!toRow(primary).amountMatched,
        confidence: matched?.confidence || 'possible',
      };
    },
    selectedSummary() {
      return this.selectedGroup ? this.summaryState(this.selectedGroup) : null;
    },
    selectedDraftRows() {
      if (!this.selectedGroup) return [];
      if (this.relatedDraftBills.length < 2) return this.draftMainRows(this.selectedGroup);
      return this.combinedDraftRows();
    },
    selectedMailRows() {
      if (!this.selectedGroup) return [];
      const amount = this.relatedDraftBills.length > 1 ? this.combinedSelectedAmount : undefined;
      return this.capturedMailRows(this.selectedGroup, amount);
    },
    selectedAttachments() {
      return this.selectedGroup ? this.capturedMailAttachments(this.selectedGroup) : [];
    },
    selectedMailHasAttachments() {
      return !!this.selectedGroup?.matches?.[0]?.email?.hasAttachments;
    },
    selectedRelatedMails() {
      const remaining = this.remainingMailsForSelectedBill;
      if (!remaining.length) return [];
      const scoredRows = new Map(
        (this.selectedGroup?.matches || []).map((row) => [String(row?.email?.id || ''), row])
      );
      return remaining.map((email) =>
        scoredRows.get(String(email.id)) || {
          email,
          score: 0,
          reasons: ['Captured mail'],
          amountMatched: false,
        }
      );
    },
    selectedRelatedMailIndex() {
      const list = this.selectedRelatedMails;
      const mailId = this.selectedRelatedMailId;
      const idx = list.findIndex((row) => String(row?.email?.id) === mailId);
      return idx < 0 ? 0 : idx;
    },
    selectedRelatedMailId() {
      return String(this.selectedGroup?.matches?.[0]?.email?.id || '');
    },
    showRelatedMailPager() {
      return this.selectedRelatedMails.length > RELATED_MAIL_PAGE_SIZE;
    },
    showRelatedMailNav() {
      return this.selectedRelatedMails.length > 1;
    },
    visibleRelatedMails() {
      const list = this.selectedRelatedMails;
      if (!this.showRelatedMailPager) return list;
      const start = Math.floor(this.selectedRelatedMailIndex / RELATED_MAIL_PAGE_SIZE) * RELATED_MAIL_PAGE_SIZE;
      return list.slice(start, start + RELATED_MAIL_PAGE_SIZE);
    },
    canGoRelatedPrev() {
      return this.selectedRelatedMailIndex > 0;
    },
    canGoRelatedNext() {
      return this.selectedRelatedMailIndex < this.selectedRelatedMails.length - 1;
    },
    canCreateBill() {
      return !!(this.companyId && this.selectedGroup?.bill?.id && this.selectedGroup?.bill?.vendorId);
    },
    selectedSourcePanels() {
      if (!this.selectedGroup || this.selectedSummary?.loading) return [];
      const source = capturedSourceFromSummary(this.parsedSummary(this.selectedGroup), {
        mailBody:
          this.selectedSummary?.mailBody ||
          this.selectedGroup?.matches?.[0]?.email?.bodyPreview ||
          '',
        extracts: this.selectedSummary?.extracts || [],
      });
      const panels = [];
      if (source.mailBody) {
        panels.push({
          key: 'mail-body',
          title: 'Mail body',
          error: '',
          blocks: toReadableSourceBlocks(source.mailBody),
        });
      }
      if (source.attachmentOcr) {
        const ocrName = source.extracts.length === 1 ? source.extracts[0].name : '';
        panels.push({
          key: 'ocr',
          title: ocrName ? `Attachment OCR — ${ocrName}` : 'Attachment OCR',
          error: '',
          blocks: toReadableSourceBlocks(source.attachmentOcr),
        });
      } else {
        source.extracts.forEach((item, idx) => {
          if (!item.error || item.text) return;
          panels.push({
            key: `ocr-${idx}`,
            title: item.name ? `Attachment OCR — ${item.name}` : 'Attachment OCR',
            error: item.error,
            blocks: [],
          });
        });
      }
      return panels.filter((panel) => panel.blocks.length || panel.error);
    },
    matchSignature() {
      return (this.matchResult?.groups || [])
        .map((group) => `${group.id}:${group?.matches?.[0]?.email?.id || ''}`)
        .join('|');
    },
    selectedCaptureKey() {
      return this.selectedGroup ? this.captureKey(this.selectedGroup) : '';
    },
  },
  watch: {
    companyId: {
      immediate: true,
      handler() {
        this.loadReviewState();
      },
    },
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) this.refreshAll();
      },
    },
    emails() {
      this.emitMatchesUpdated();
    },
    matchSignature() {
      this.emitMatchesUpdated();
      if (!this.active) return;
      this.$nextTick(() => {
        if (!this.active) return;
        this.autoExtractReadyAttachments(this.matchResult.groups);
        this.autoLoadGroupAttachments(this.matchResult.groups);
      });
    },
    selectedCaptureKey() {
      if (!this.active || !this.selectedGroup) return;
      this.$nextTick(() => {
        if (!this.active || !this.selectedGroup) return;
        this.autoExtractReadyAttachments([this.selectedGroup]);
        this.autoLoadGroupAttachments([this.selectedGroup]);
      });
    },
    selectedRelatedMailId() {
      this.$nextTick(() => this.scrollActiveRelatedChipIntoView());
    },
  },
  beforeUnmount() {
    Object.values(this.reviewPolls || {}).forEach((timer) => {
      if (timer && timer !== 1) clearTimeout(timer);
    });
  },
  methods: {
    goToReviewDraft(group) {
      const bill = group?.bill;
      if (!this.companyId || !bill?.id || !bill?.vendorId) return;
      const messageId = group?.matches?.[0]?.email?.id || '';
      const query = {
        billId: String(bill.id),
        prefillVendor: String(bill.vendorId),
        vendorName: bill.vendorName || 'Vendor',
        fromCaptured: '1',
      };
      if (messageId) query.capturedMessageId = String(messageId);
      const relatedIds = (this.relatedDraftBills || [])
        .map((item) => item?.id)
        .filter((id) => id != null && String(id) !== String(bill.id));
      if (relatedIds.length) query.relatedBillIds = relatedIds.map((id) => String(id)).join(',');
      this.$router.push({
        name: 'CreateBill',
        params: { id: this.companyId },
        query,
      });
    },
    emitMatchesUpdated() {
      const groups = this.matchResult?.groups || [];
      const billIds = new Set(
        groups
          .map((g) => g?.bill?.id)
          .filter((id) => id != null)
          .map((id) => String(id))
      );
      if (this.hasSelectedBill && this.remainingMailsForSelectedBill.length) {
        billIds.add(String(this.selectedBillId));
      }
      this.$emit('matches-updated', {
        count: billIds.size,
        billIds: [...billIds],
      });
    },
    captureKey(group) {
      const mailId = group?.matches?.[0]?.email?.id;
      if (group?.id && mailId) return `${group.id}::${mailId}`;
      return group?.id || '';
    },
    reviewStorageKey() {
      return this.companyId ? `annabooks-captured-review:${this.companyId}` : '';
    },
    loadReviewState() {
      const key = this.reviewStorageKey();
      if (!key) {
        this.dismissedPairs = [];
        this.preferredMailByBillId = {};
        return;
      }
      try {
        const parsed = JSON.parse(localStorage.getItem(key) || 'null');
        this.dismissedPairs = Array.isArray(parsed?.dismissed) ? parsed.dismissed.map(String) : [];
        this.preferredMailByBillId =
          parsed?.preferred && typeof parsed.preferred === 'object' && !Array.isArray(parsed.preferred)
            ? { ...parsed.preferred }
            : {};
      } catch (error) {
        this.dismissedPairs = [];
        this.preferredMailByBillId = {};
      }
    },
    saveReviewState() {
      const key = this.reviewStorageKey();
      if (!key) return;
      try {
        localStorage.setItem(
          key,
          JSON.stringify({
            dismissed: this.dismissedPairs,
            preferred: this.preferredMailByBillId,
          })
        );
      } catch (error) {
        /* ignore quota */
      }
    },
    restoreHiddenMatches() {
      this.dismissedPairs = [];
      this.saveReviewState();
    },
    relatedMailLabel(email) {
      const subject = String(email?.subject || '').trim();
      if (subject) return subject.length > 44 ? `${subject.slice(0, 42)}…` : subject;
      const when = this.formatMailDateTime(email);
      if (when) return when;
      return mailSenderEmail(email) || 'Related mail';
    },
    isRelatedMailSelected(row) {
      const id = row?.email?.id;
      return id != null && this.selectedRelatedMailId !== '' && String(id) === this.selectedRelatedMailId;
    },
    scrollActiveRelatedChipIntoView() {
      const list = this.$refs.relatedMailList;
      if (!list || typeof list.querySelector !== 'function') return;
      const active = list.querySelector('.captured-group__related-chip--active');
      if (!active) return;
      const left = active.offsetLeft - list.clientWidth / 2 + active.offsetWidth / 2;
      list.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    },
    selectRelatedMail(group, row) {
      const billId = group?.bill?.id;
      const mailId = row?.email?.id;
      if (billId == null || mailId == null) return;
      if (String(group?.matches?.[0]?.email?.id) === String(mailId)) return;
      this.preferredMailByBillId = {
        ...this.preferredMailByBillId,
        [String(billId)]: String(mailId),
      };
      this.saveReviewState();
      this.$nextTick(() => {
        const next = (this.matchResult.groups || []).find(
          (g) => String(g?.bill?.id) === String(billId)
        );
        if (next) {
          this.loadGroupAttachments(next);
          this.extractAttachmentSummary(next, { auto: true });
        }
      });
    },
    stepRelatedMail(delta) {
      const list = this.selectedRelatedMails;
      const next = list[this.selectedRelatedMailIndex + Number(delta)];
      if (!next || !this.selectedGroup) return;
      this.selectRelatedMail(this.selectedGroup, next);
    },
    clearCapturedMatch(group) {
      const billId = group?.bill?.id;
      const mailId = group?.matches?.[0]?.email?.id;
      if (billId == null || mailId == null) return;
      const key = capturePairKey(billId, mailId);
      const dismissed = this.dismissedPairs.includes(key)
        ? this.dismissedPairs
        : [...this.dismissedPairs, key];
      const remaining = (this.emails || []).filter((email) => {
        if (!email?.id || String(email.id) === String(mailId)) return false;
        return !dismissed.includes(capturePairKey(billId, email.id));
      });
      const nextId = remaining[0]?.id;
      const preferred = { ...this.preferredMailByBillId };
      if (nextId) preferred[String(billId)] = String(nextId);
      else delete preferred[String(billId)];
      this.dismissedPairs = dismissed;
      this.preferredMailByBillId = preferred;
      this.saveReviewState();
      this.statusMessage = nextId
        ? 'Skipped. Showing the next mail for this bill.'
        : 'No more mail left for this bill. Restore hidden matches to review again.';
      this.statusType = 'info';
      setTimeout(() => {
        if (this.statusType === 'info') {
          this.statusMessage = '';
          this.statusType = '';
        }
      }, 3500);
    },
    summaryState(group) {
      const key = typeof group === 'string' ? group : this.captureKey(group);
      return key ? this.attachmentSummaries[key] || null : null;
    },
    parsedSummary(group) {
      const raw = this.summaryState(group)?.summary || '';
      return parseBillAttachmentSummary(raw);
    },
    consultantRecords(bill) {
      return bill?.details?.consultantRecords || [];
    },
    billDetailsForGroup(group, amountOverride) {
      const bill = group?.bill || {};
      const consultants = this.consultantRecords(bill)
        .map((r) => r.consultantName)
        .filter(Boolean);
      const hours = this.consultantRecords(bill).reduce(
        (sum, row) => sum + (parseFloat(row.hours) || 0),
        0
      );
      const card = toBillDetailsCard(this.parsedSummary(group), {
        formatCurrency: (n) => this.formatCurrency(n),
        draft: {
          vendorName: bill.vendorName,
          vendorEmail: group.vendorEmail,
          originalAmount: amountOverride != null ? amountOverride : bill.originalAmount,
          date: bill.date,
          consultants,
          hours,
        },
      });

      const email = group?.matches?.[0]?.email;
      const mailRow = {
        key: 'mail',
        label: 'Mail',
        value: this.mailWithTimeLabel(email),
        matchStatus: this.mailMatchStatus(group),
      };
      const rows = [...(card.rows || [])];
      const vendorIdx = rows.findIndex((r) => r.key === 'vendor');
      if (vendorIdx >= 0) rows.splice(vendorIdx + 1, 0, mailRow);
      else rows.unshift(mailRow);

      return { ...card, rows };
    },
    capturedMailRows(group, amountOverride) {
      const email = group?.matches?.[0]?.email;
      const mailRow = {
        key: 'mail',
        label: 'Mail',
        value: this.mailWithTimeLabel(email),
        matchStatus: this.mailMatchStatus(group),
      };
      if (!this.summaryState(group)?.summary) {
        return email ? [mailRow] : [];
      }
      return this.billDetailsForGroup(group, amountOverride).rows;
    },
    combinedDraftRows() {
      const group = this.selectedGroup;
      const bills = this.relatedDraftBills;
      const primary = group?.bill || bills[0] || {};
      const statusByKey = this.matchStatusByKey(group);
      const totalStatus = this.combinedTotalMatchStatus();
      const billLines = bills
        .map((bill) => {
          const no = bill.no || 'Bill';
          const detail = this.lineDetailsText(bill);
          const amount = this.formatCurrency(bill.originalAmount);
          const body = detail && detail !== '—' ? detail : '';
          return [no, body, amount].filter(Boolean).join('\n');
        })
        .join('\n\n');
      return [
        {
          key: 'vendor',
          label: 'Vendor',
          value: primary.vendorName || bills[0]?.vendorName || '—',
          matchStatus: statusByKey.vendor || 'skip',
        },
        {
          key: 'bills',
          label: 'Bills',
          value: `${bills.length} draft bills for this captured mail`,
          matchStatus: 'skip',
        },
        {
          key: 'details',
          label: 'Line details',
          value: billLines || '—',
          matchStatus: 'skip',
        },
        {
          key: 'total',
          label: 'Total',
          value: this.formatCurrency(this.combinedSelectedAmount),
          matchStatus: totalStatus,
        },
      ];
    },
    combinedTotalMatchStatus() {
      const extracted = this.selectedSummary?.extractedAmount;
      const status = compareDraftAndExtractedTotal(this.combinedSelectedAmount, extracted).status;
      if (status === 'match') return 'match';
      if (status === 'mismatch') return 'mismatch';
      return 'skip';
    },
    mailFromLabel(email) {
      const name = email?.fromName || '';
      const addr = mailSenderEmail(email);
      if (name && addr) return `${name} <${addr}>`;
      return name || addr || '—';
    },
    formatMailDateTime(email) {
      const value = email?.receivedDateTime || email?.date;
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    },
    mailWithTimeLabel(email) {
      const from = this.mailFromLabel(email);
      const when = this.formatMailDateTime(email);
      if (from && when) return `${from}\n${when}`;
      return from || when || '—';
    },
    vendorRecordFor(bill) {
      const id = bill?.vendorId;
      if (id == null) return null;
      return (this.vendors || []).find((v) => String(v.id) === String(id)) || null;
    },
    paymentTermRecord(bill) {
      const vendor = this.vendorRecordFor(bill);
      const termId =
        bill?.paymentTerm ||
        bill?.paymentTermId ||
        vendor?.paymentTermId ||
        vendor?.paymentTerm?.id ||
        vendor?.PaymentTerm?.id ||
        null;
      if (termId == null || termId === '') return null;
      return (this.paymentTerms || []).find((t) => String(t.id) === String(termId)) || null;
    },
    paymentTermsLabel(bill) {
      const term = this.paymentTermRecord(bill);
      const vendor = this.vendorRecordFor(bill);
      const name =
        bill?.paymentTermName ||
        bill?.paymentTermLabel ||
        bill?.paymentTerm?.name ||
        bill?.PaymentTerm?.name ||
        term?.name ||
        vendor?.paymentTermName ||
        vendor?.paymentTerm?.name ||
        vendor?.PaymentTerm?.name ||
        '';
      const lines = [];
      if (String(name).trim()) lines.push(String(name).trim());
      if (term?.dueDays != null && term.dueDays !== '') {
        const days = Number(term.dueDays);
        if (Number.isFinite(days)) {
          lines.push(`Net ${days} · due in ${days} day${days === 1 ? '' : 's'}`);
        }
      }
      if (term?.description && String(term.description).trim() && String(term.description).trim() !== String(name).trim()) {
        lines.push(String(term.description).trim());
      }
      if (bill?.dueDate) {
        const due = formatUsDate(bill.dueDate);
        if (due) lines.push(`Due date ${due}`);
      }
      if (vendor?.paymentMethod) {
        lines.push(`Pay by ${vendor.paymentMethod}`);
      }
      return lines.length ? lines.join('\n') : '—';
    },
    lineDetailsText(bill) {
      if (bill?.loadingDetails) return 'Loading…';
      const consultants = this.consultantRecords(bill);
      const displayLines = getBillDisplayLines(bill?.details || {});
      if (!displayLines.length && !consultants.length) return '—';

      const consultantByName = new Map(
        consultants.map((row) => [String(row.consultantName || '').trim().toLowerCase(), row])
      );

      return displayLines
        .map((line) => {
          const name = String(line.name || '').trim();
          const consultant = consultantByName.get(name.toLowerCase());
          const rows = [];
          if (line.lineType && line.lineType !== 'Consultant') {
            rows.push(line.lineType);
          }
          if (name) rows.push(name);

          if (consultant) {
            const from = formatUsDate(consultant.fromDate);
            const to = formatUsDate(consultant.toDate);
            let period = '';
            if (from && to) period = `${from} – ${to}`;
            else if (from || to) period = from || to;
            else {
              period = formatUsPeriodText(consultant.description || '')
                .replace(/\s*\(\s*[\d.]+\s*hrs?\s*\)\s*/gi, '')
                .replace(/\s*[–—]\s*/g, ' – ')
                .trim();
            }
            if (period) rows.push(period);
            const desc = String(consultant.description || '').trim();
            if (desc && desc !== period && (!period || !desc.includes(period))) {
              rows.push(desc);
            }
          } else if (line.description && line.description !== '-' && line.description !== name) {
            rows.push(String(line.description).trim());
          }

          const hoursNum = parseFloat(line.hours);
          const rateNum = parseFloat(line.hourlyRate);
          const qtyNum = parseFloat(line.quantity);
          const unitNum = parseFloat(line.unitPrice);
          const meta = [];
          if (Number.isFinite(hoursNum) && hoursNum > 0) {
            meta.push(`${Number.isInteger(hoursNum) ? hoursNum : hoursNum.toFixed(2)} hrs`);
          }
          if (Number.isFinite(rateNum) && rateNum > 0) {
            meta.push(`${this.formatCurrency(rateNum)}/hr`);
          }
          if (Number.isFinite(qtyNum) && qtyNum > 0 && Number.isFinite(unitNum)) {
            meta.push(`${qtyNum} × ${this.formatCurrency(unitNum)}`);
          }
          if (meta.length) rows.push(meta.join(' · '));
          if (line.amount != null && line.amount !== '') {
            rows.push(this.formatCurrency(line.amount));
          }
          return rows.filter(Boolean).join('\n');
        })
        .filter(Boolean)
        .join('\n\n');
    },
    billDetailsSummary(bill) {
      const rows = this.consultantRecords(bill);
      if (bill?.loadingDetails) return 'Loading…';
      if (!rows.length) {
        const accounts = bill?.details?.accountRecords?.length || 0;
        const items = bill?.details?.itemRecords?.length || 0;
        const n = accounts + items;
        return n > 0 ? `${n} line${n === 1 ? '' : 's'}` : '—';
      }
      return rows
        .map((row) => {
          const name = row.consultantName || 'Consultant';
          const from = formatUsDate(row.fromDate);
          const to = formatUsDate(row.toDate);
          let period = '';
          if (from && to) period = `${from} - ${to}`;
          else if (from || to) period = from || to;
          else {
            period = formatUsPeriodText(row.description || '')
              .replace(/\s*\(\s*[\d.]+\s*hrs?\s*\)\s*/gi, '')
              .replace(/\s*[–—]\s*/g, ' - ')
              .trim();
          }
          const hoursNum = parseFloat(row.hours);
          const hoursLabel = Number.isFinite(hoursNum)
            ? `${Number.isInteger(hoursNum) ? hoursNum : hoursNum.toFixed(2)} hrs`
            : '';
          const rateNum = parseFloat(row.hourlyRate ?? row.rate);
          const rateLabel = Number.isFinite(rateNum) && rateNum > 0
            ? `${this.formatCurrency(rateNum)}/hr`
            : '';
          const meta = [hoursLabel, rateLabel].filter(Boolean).join(' · ');
          return [name, period, meta].filter(Boolean).join('\n');
        })
        .join('\n\n');
    },
    mailMatchStatus(group) {
      const draftEmail = String(group?.vendorEmail || '').trim().toLowerCase();
      const sender = String(mailSenderEmail(group?.matches?.[0]?.email) || '').trim().toLowerCase();
      if (!draftEmail || !sender) return 'skip';
      return draftEmail === sender ? 'match' : 'mismatch';
    },
    matchStatusByKey(group) {
      const statusByKey = { mail: this.mailMatchStatus(group) };
      if (!this.summaryState(group)?.summary) return statusByKey;
      (this.billDetailsForGroup(group).rows || []).forEach((row) => {
        if (row?.key && row.matchStatus && row.matchStatus !== 'skip') {
          statusByKey[row.key] = row.matchStatus;
        }
      });
      return statusByKey;
    },
    draftMainRows(group) {
      const bill = group?.bill || {};
      const statusByKey = this.matchStatusByKey(group);
      const lineDetails = this.lineDetailsText(bill);
      return [
        {
          key: 'vendor',
          label: 'Vendor',
          value: bill.vendorName || '—',
          matchStatus: statusByKey.vendor || 'skip',
        },
        {
          key: 'mail',
          label: 'Mail',
          value: group.vendorEmail || '—',
          matchStatus: statusByKey.mail || 'skip',
        },
        {
          key: 'date',
          label: 'Date',
          value: formatUsDate(bill.date) || '—',
          matchStatus: 'skip',
        },
        {
          key: 'terms',
          label: 'Payment Terms',
          value: this.paymentTermsLabel(bill),
          matchStatus: 'skip',
        },
        {
          key: 'details',
          label: 'Line details',
          value: lineDetails || this.billDetailsSummary(bill),
          matchStatus: statusByKey.labor || 'skip',
        },
        {
          key: 'total',
          label: 'Total',
          value: this.formatCurrency(bill.originalAmount),
          matchStatus: statusByKey.total || 'skip',
        },
      ];
    },
    formatCurrency(value) {
      const amount = Number(value) || 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(amount);
    },
    reviewProviderLabel(data) {
      const provider = data?.provider || '';
      const model = data?.model || 'qwen2.5:1.5b';
      if (provider === 'openai-fallback') {
        return `Fallback · ${model}${data?.ollamaError ? ' (Ollama offline)' : ''}`;
      }
      return `Small Qwen · ${model || 'qwen2.5:1.5b'}`;
    },
    applyReviewToGroup(group, data) {
      if (!group || !data) return;
      const key = this.captureKey(group);
      if (!key) return;
      const messageId = data.messageId || group?.matches?.[0]?.email?.id;
      const failed = data.status === 'failed';
      const processing = data.status === 'processing' || data.status === 'pending';
      this.attachmentSummaries = {
        ...this.attachmentSummaries,
        [key]: {
          loading: processing && !data.summary,
          attempted: true,
          mailId: messageId,
          summary: data.summary || '',
          extractedAmount: extractSummaryTotal(data.summary || ''),
          extracts: data.extracts || [],
          mailBody: data.mailBody || '',
          provider: data.provider || '',
          model: data.model || '',
          providerLabel: this.reviewProviderLabel(data),
          error: failed ? data.error || 'Review failed' : '',
          billAttachmentCount: Array.isArray(data.billAttachments) ? data.billAttachments.length : 0,
        },
      };
      if (Array.isArray(data.attachments) && data.attachments.length) {
        this.storeGroupAttachments(group, data.attachments);
      }
    },
    async fetchSavedReview(messageId) {
      if (!messageId || !this.companyId) return null;
      const url = this.withCompanyQuery(
        `${process.env.VUE_APP_API_URL}/outlook/bill-mail/reviews?messageIds=${encodeURIComponent(messageId)}`
      );
      const response = await fetch(url);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) return null;
      const rows = Array.isArray(result.data) ? result.data : [];
      return rows.find((row) => String(row.messageId) === String(messageId)) || null;
    },
    async hydrateSavedReviews() {
      const ids = [...new Set((this.emails || []).map((email) => email?.id).filter(Boolean).map(String))];
      if (!ids.length || !this.companyId) return {};
      const url = this.withCompanyQuery(
        `${process.env.VUE_APP_API_URL}/outlook/bill-mail/reviews?messageIds=${encodeURIComponent(ids.join(','))}`
      );
      try {
        const response = await fetch(url);
        const result = await response.json().catch(() => ({}));
        if (!response.ok) return {};
        const rows = Array.isArray(result.data) ? result.data : [];
        const byId = Object.create(null);
        rows.forEach((row) => {
          byId[String(row.messageId)] = row;
        });
        (this.matchResult.groups || []).forEach((group) => {
          const mailId = group?.matches?.[0]?.email?.id;
          if (mailId && byId[String(mailId)]) this.applyReviewToGroup(group, byId[String(mailId)]);
        });
        return byId;
      } catch (error) {
        logger.error('Could not load saved captured mail reviews:', error);
        return {};
      }
    },
    clearReviewPoll(messageId) {
      const key = String(messageId || '');
      const timer = this.reviewPolls[key];
      if (timer && timer !== 1) clearTimeout(timer);
      const next = { ...this.reviewPolls };
      delete next[key];
      this.reviewPolls = next;
    },
    pollSavedReview(group) {
      const messageId = group?.matches?.[0]?.email?.id;
      if (!messageId) return;
      const key = String(messageId);
      if (this.reviewPolls[key]) return;
      this.reviewPolls = { ...this.reviewPolls, [key]: 1 };
      let tries = 0;
      const tick = async () => {
        tries += 1;
        try {
          const row = await this.fetchSavedReview(messageId);
          if (row?.status === 'ready' || row?.status === 'failed') {
            this.applyReviewToGroup(group, row);
            this.clearReviewPoll(key);
            return;
          }
          if (!row && tries >= 2) {
            this.clearReviewPoll(key);
            this.extractAttachmentSummary(group, { auto: true });
            return;
          }
        } catch (error) {
          logger.error('Captured mail review poll failed:', error);
        }
        if (tries >= 20) {
          this.clearReviewPoll(key);
          this.extractAttachmentSummary(group, { auto: true });
          return;
        }
        this.reviewPolls = {
          ...this.reviewPolls,
          [key]: setTimeout(tick, 2500),
        };
      };
      tick();
    },
    autoExtractReadyAttachments(groups) {
      this.hydrateSavedReviews().then((byId) => {
        (groups || []).forEach((group) => {
          const email = group?.matches?.[0]?.email;
          if (!email?.id) return;
          const saved = byId[String(email.id)];
          const state = this.summaryState(group);
          if (state?.summary || saved?.status === 'ready') return;
          if (saved?.status === 'processing' || saved?.status === 'pending' || email.hasAttachments) {
            this.pollSavedReview(group);
          }
        });
      });
    },
    mailServiceFor(email) {
      const tagged = String(email?.mailService || '').toLowerCase();
      if (tagged === 'gmail' || tagged === 'outlook') return tagged;
      return 'outlook';
    },
    withCompanyQuery(url) {
      if (!this.companyId) return url;
      const join = url.includes('?') ? '&' : '?';
      return `${url}${join}companyId=${encodeURIComponent(this.companyId)}`;
    },
    normalizeAttachmentList(list) {
      return (Array.isArray(list) ? list : [])
        .filter((attachment) => attachment && (attachment.id || attachment.filePath))
        .map((attachment) => ({
          id: attachment.id || attachment.filePath,
          name: attachment.name || attachment.fileName || 'attachment',
          contentType: attachment.contentType || attachment.mimeType || 'application/octet-stream',
          size: attachment.size || attachment.fileSize || 0,
          filePath: attachment.filePath || null,
        }));
    },
    storeGroupAttachments(group, list) {
      const key = typeof group === 'string' ? group : this.captureKey(group);
      if (!key) return;
      const next = this.normalizeAttachmentList(list);
      if (!next.length) return;
      this.groupAttachments = {
        ...this.groupAttachments,
        [key]: next,
      };
    },
    capturedMailAttachments(group) {
      const stored = this.groupAttachments[this.captureKey(group)];
      if (Array.isArray(stored) && stored.length) return stored;
      const email = group?.matches?.[0]?.email;
      return this.normalizeAttachmentList(email?.attachments);
    },
    autoLoadGroupAttachments(groups) {
      (groups || []).forEach((group) => this.loadGroupAttachments(group));
    },
    async loadGroupAttachments(group) {
      const email = group?.matches?.[0]?.email;
      if (!group?.id || !email?.id) return;
      if (this.capturedMailAttachments(group).length) return;
      if (!email.hasAttachments) return;

      const service = this.mailServiceFor(email);
      let url = `${process.env.VUE_APP_API_URL}/${service}/read/${encodeURIComponent(email.id)}`;
      if (service === 'outlook') url = this.withCompanyQuery(url);

      try {
        const response = await fetch(url);
        const result = await response.json().catch(() => ({}));
        if (!response.ok) return;
        const details = result.data || result;
        this.storeGroupAttachments(group, details?.attachments);
      } catch (error) {
        logger.error('Failed to load captured mail attachments:', error);
      }
    },
    formatFileSize(bytes) {
      const size = Number(bytes) || 0;
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },
    attachmentFileIcon(name) {
      const ext = String(name || '').split('.').pop()?.toLowerCase() || '';
      if (ext === 'pdf') return 'bi bi-file-earmark-pdf-fill';
      if (['doc', 'docx'].includes(ext)) return 'bi bi-file-earmark-word-fill';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'bi bi-file-earmark-excel-fill';
      if (['ppt', 'pptx'].includes(ext)) return 'bi bi-file-earmark-ppt-fill';
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'bi bi-file-earmark-image-fill';
      if (['zip', 'rar', '7z'].includes(ext)) return 'bi bi-file-earmark-zip-fill';
      return 'bi bi-file-earmark-fill';
    },
    attachmentCardClass(name) {
      const ext = String(name || '').split('.').pop()?.toLowerCase() || '';
      if (ext === 'pdf') return 'mail-attachment-card--pdf';
      if (['doc', 'docx'].includes(ext)) return 'mail-attachment-card--doc';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'mail-attachment-card--sheet';
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'mail-attachment-card--image';
      return 'mail-attachment-card--file';
    },
    async fetchCapturedAttachmentBlob(group, attachment) {
      if (attachment?.filePath) {
        const filename = String(attachment.filePath).includes('/')
          ? String(attachment.filePath).split('/').pop()
          : attachment.filePath;
        const url = this.withCompanyQuery(
          `${process.env.VUE_APP_API_URL}/vendor/documents/${encodeURIComponent(filename)}`
        );
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to open saved bill attachment');
        return response.blob();
      }

      const email = group?.matches?.[0]?.email;
      if (!email?.id || !attachment?.id) throw new Error('Attachment is not available');
      const service = this.mailServiceFor(email);
      const base = service === 'gmail' ? '/gmail/read' : '/outlook/read';
      let url = `${process.env.VUE_APP_API_URL}${base}/${email.id}/attachments/${attachment.id}`;
      if (service === 'gmail') {
        url += `?filename=${encodeURIComponent(attachment.name || 'attachment')}&contentType=${encodeURIComponent(attachment.contentType || 'application/octet-stream')}`;
      } else {
        url = this.withCompanyQuery(url);
      }
      const response = await fetch(url);
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Failed to download attachment');
      }
      return response.blob();
    },
    async openCapturedAttachment(group, attachment) {
      if (!attachment) return;
      this.activeAttachmentId = attachment.id;
      try {
        const blob = await this.fetchCapturedAttachmentBlob(group, attachment);
        const objectUrl = URL.createObjectURL(blob);
        const opened = window.open(objectUrl, '_blank', 'noopener');
        if (!opened) {
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = attachment.name || 'attachment';
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch (error) {
        logger.error('Could not open captured mail attachment:', error);
        this.statusMessage = `Could not open attachment: ${error.message}`;
        this.statusType = 'error';
        setTimeout(() => {
          if (this.statusType === 'error') {
            this.statusMessage = '';
            this.statusType = '';
          }
        }, 4000);
      } finally {
        this.activeAttachmentId = null;
      }
    },
    async extractAttachmentSummary(group, options = {}) {
      const email = group?.matches?.[0]?.email;
      const messageId = email?.id;
      if (!messageId || !this.companyId) return;
      const key = this.captureKey(group);
      if (!key) return;

      if (!options.force) {
        try {
          const saved = await this.fetchSavedReview(messageId);
          if (saved?.status === 'ready' && (saved.summary || saved.mailBody)) {
            this.applyReviewToGroup(group, saved);
            return;
          }
          if (saved?.status === 'processing' || saved?.status === 'pending') {
            this.applyReviewToGroup(group, saved);
            this.pollSavedReview(group);
            return;
          }
        } catch (error) {
          logger.error('Could not read saved captured mail review:', error);
        }
      }

      this.attachmentSummaries = {
        ...this.attachmentSummaries,
        [key]: {
          ...(this.attachmentSummaries[key] || {}),
          loading: true,
          attempted: true,
          mailId: messageId,
          error: '',
          summary: options.force ? '' : this.attachmentSummaries[key]?.summary || '',
        },
      };

      try {
        const url = `${process.env.VUE_APP_API_URL}/outlook/bill-mail/summarize?companyId=${encodeURIComponent(this.companyId)}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId: this.companyId,
            messageId,
            draftBill: {
              id: group.bill?.id,
              vendorName: group.bill?.vendorName,
              vendorEmail: group.vendorEmail,
              no: group.bill?.no,
              originalAmount: group.bill?.originalAmount,
              date: group.bill?.date,
            },
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) {
          throw new Error(result.error || result.message || 'Summary failed');
        }

        const provider = result.data?.provider || '';
        const model = result.data?.model || '';
        let providerLabel = `Small Qwen · ${model}`;
        if (provider === 'openai-vision') {
          providerLabel = `Vision OCR · ${model}`;
        } else if (provider === 'openai-fallback') {
          providerLabel = `Fallback · ${model}${result.data?.ollamaError ? ' (Ollama offline)' : ''}`;
        } else if (provider === 'ollama') {
          providerLabel = `Small Qwen · ${model}`;
        }

        const summaryText = result.data?.summary || '';
        this.attachmentSummaries = {
          ...this.attachmentSummaries,
          [key]: {
            loading: false,
            attempted: true,
            mailId: messageId,
            summary: summaryText,
            extractedAmount: extractSummaryTotal(summaryText),
            extracts: result.data?.extracts || [],
            mailBody: result.data?.mailBody || '',
            provider,
            model,
            providerLabel,
            error: '',
            billAttachmentCount: Array.isArray(result.data?.billAttachments)
              ? result.data.billAttachments.length
              : 0,
          },
        };
        const extracted = Array.isArray(result.data?.attachments) ? result.data.attachments : [];
        if (extracted.length) {
          this.storeGroupAttachments(group, extracted);
        } else {
          this.loadGroupAttachments(group);
        }
      } catch (error) {
        logger.error('Small Qwen attachment summary failed:', error);
        this.attachmentSummaries = {
          ...this.attachmentSummaries,
          [key]: {
            loading: false,
            attempted: true,
            mailId: messageId,
            summary: '',
            error:
              error.message ||
              'Could not extract summary. Use Re-extract after fixing the PDF or API.',
          },
        };
        this.loadGroupAttachments(group);
      }
    },
    async refreshAll() {
      this.$emit('refresh-mail');
      await this.fetchDrafts();
      const n = this.matchResult.groups.length;
      this.statusMessage =
        n > 0
          ? `${n} draft–mail group(s) ready to review.`
          : 'No strong draft–mail matches yet.';
      this.statusType = n > 0 ? 'success' : 'info';
      this.$nextTick(() => {
        this.autoExtractReadyAttachments(this.matchResult.groups);
        this.autoLoadGroupAttachments(this.matchResult.groups);
      });
      setTimeout(() => {
        if (this.statusMessage) {
          this.statusMessage = '';
          this.statusType = '';
        }
      }, 4000);
    },
    async fetchDrafts() {
      if (!this.companyId) return;
      this.loading = true;
      try {
        const [vendors, bills, termsRes] = await Promise.all([
          ApiService.getAllVendors(this.companyId),
          ApiService.getBillsAndExpenses(this.companyId, { view: 'All', dateFilter: 'All' }),
          ApiService.getAllPaymentTerms(this.companyId).catch(() => null),
        ]);
        this.vendors = Array.isArray(vendors) ? vendors : [];
        const termsPayload = termsRes?.paymentTerms || termsRes?.data?.paymentTerms || termsRes;
        this.paymentTerms = Array.isArray(termsPayload) ? termsPayload : [];
        const all = Array.isArray(bills) ? bills : [];
        this.drafts = all
          .filter((r) => {
            if (!(r.type === 'Bill' && String(r.status || '').toLowerCase() === 'draft')) return false;
            // Portal Submitted stays in Invites & Notes — Captured is Draft vs mail only
            if (r.isVendorSubmitted === true) return false;
            if (r.isVendorPortalDraft) return false;
            if (r.hasVendorInvoiceTimesheet === true) return false;
            return true;
          })
          .map((record) => ({
            ...record,
            loadingDetails: false,
            details: null,
          }));
        await this.loadDraftDetails(this.drafts);
      } catch (error) {
        logger.error('Captured panel failed to load drafts:', error);
        this.drafts = [];
        this.vendors = [];
        this.paymentTerms = [];
      } finally {
        this.loading = false;
      }
    },
    async loadDraftDetails(list = []) {
      const pending = (list || []).filter((record) => record && !record.details && !record.loadingDetails);
      if (!pending.length) return;
      await Promise.all(
        pending.map(async (record) => {
          record.loadingDetails = true;
          try {
            await ensureRecordDetails(record);
          } catch (error) {
            logger.error('Captured panel bill details failed:', error);
            record.details = { accountRecords: [], itemRecords: [], consultantRecords: [] };
          } finally {
            record.loadingDetails = false;
          }
        })
      );
    },
  },
};
</script>

<style scoped>
.draft-bills-captured__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  background: #fff;
}

.draft-bills-captured__empty-actions {
  display: inline-flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
}

.draft-bills-captured__reading {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background: #fff;
}

.draft-bills-captured__scroll {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  min-height: 0;
  padding: 0;
  background: #fff;
}

.captured-group__related--top {
  position: static;
  flex-shrink: 0;
  margin: 0;
  padding: 0.55rem 1.1rem 0.6rem;
  background: #faf9f8;
  border-bottom: 1px solid #edebe9;
}

.captured-reading-cmd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 0.7rem 1.1rem 0.75rem;
  background: #fff;
  border-bottom: 1px solid #edebe9;
}

.captured-reading-cmd__identity {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.captured-reading-cmd__from {
  font-size: 1rem;
  font-weight: 700;
  color: #201f1e;
  line-height: 1.3;
}

.captured-reading-cmd__subject {
  font-size: 0.78rem;
  color: #605e5c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.captured-reading-cmd__actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.captured-group__related-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.1rem;
  margin-left: 0.3rem;
  padding: 0 0.28rem;
  border-radius: 999px;
  background: #deecf9;
  color: #0078d4;
  font-size: 0.64rem;
  font-weight: 700;
}

.captured-group {
  border: none;
  background: #fff;
  margin: 0;
  padding: 1rem 1.1rem 1.25rem;
}

.captured-group--high,
.captured-group--medium,
.captured-group--possible {
  border-color: transparent;
  background: #fff;
}

.captured-group__tick {
  display: inline-flex;
  margin-left: 0.28rem;
  font-size: 0.9rem;
  color: #0e700e;
  vertical-align: middle;
  line-height: 1;
  flex-shrink: 0;
}

.captured-group__inline-tag {
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.08rem 0.32rem;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  vertical-align: middle;
}

.captured-group__inline-tag--mismatch {
  color: #8a6116;
  background: #fff4ce;
  border: none;
}

.captured-main {
  margin: 0;
  border: none;
  background: transparent;
  overflow: hidden;
}

.captured-main__row {
  display: grid;
  grid-template-columns: 7.1rem minmax(0, 1fr);
  gap: 0.45rem;
  padding: 0.42rem 0;
  border-bottom: 1px solid #f3f2f1;
  align-items: start;
}

.captured-main__row:last-child {
  border-bottom: none;
}

.captured-main__row dt {
  margin: 0;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8a8886;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1.35;
}

.captured-main__row dd {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.3rem;
  min-width: 0;
  font-size: 0.8rem;
  font-weight: 500;
  color: #323130;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.captured-main__colon {
  color: #8a8886;
  font-weight: 500;
  flex-shrink: 0;
}

.captured-main__value {
  min-width: 0;
  flex: 1;
}

.captured-main__row--total {
  background: #f3faf3;
}

.captured-main__row--total dt {
  color: #0e700e;
}

.captured-main__row--total .captured-main__value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0e700e;
}

.captured-main__row--match {
  background: #f3faf3;
}

.captured-source {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-top: 0.75rem;
}

.captured-source__card {
  border: 1px solid #d6e4f5;
  background: #f8fbff;
  overflow: hidden;
}

.captured-source__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  background: #eef5ff;
  border-bottom: 1px solid #d6e4f5;
}

.captured-source__title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #0f6cbd;
}

.captured-source__error {
  margin: 0;
  padding: 0.5rem 0.7rem;
  font-size: 0.76rem;
  color: #8a6116;
}

.captured-source__body {
  padding: 0.55rem 0.7rem 0.7rem;
  background: #fff;
}

.captured-source__h {
  margin: 0.45rem 0 0.3rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #323130;
}

.captured-source__h:first-child {
  margin-top: 0;
}

.captured-source__p {
  margin: 0 0 0.4rem;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #323130;
  white-space: pre-wrap;
  word-break: break-word;
}

.captured-source__p:last-child {
  margin-bottom: 0;
}

.captured-source__fields {
  margin: 0 0 0.45rem;
}

.captured-source__field {
  display: grid;
  grid-template-columns: 7.2rem minmax(0, 1fr);
  gap: 0.35rem 0.55rem;
  padding: 0.22rem 0;
  border-bottom: 1px solid #f3f6fa;
}

.captured-source__field:last-child {
  border-bottom: none;
}

.captured-source__field dt {
  margin: 0;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #8a8886;
}

.captured-source__field dd {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #323130;
  word-break: break-word;
}

.captured-group__pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  align-items: stretch;
}

.captured-bill-card.dbc-card {
  cursor: default;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0 1.1rem 0 0;
  border: none;
  box-shadow: none;
  background: transparent;
  overflow: visible;
  transition: none;
}

.captured-bill-card--mail.dbc-card {
  padding: 0 0 0 1.1rem;
  border-left: 1px solid #edebe9;
}

.captured-bill-card.dbc-card:hover,
.captured-bill-card.dbc-card:hover::after {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  width: auto;
}

.captured-bill-card.dbc-card::after,
.captured-bill-card.dbc-card:hover::after {
  display: none;
  content: none;
  width: 0;
}

.captured-bill-card__ribbon {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0 0.65rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0078d4;
}

.captured-bill-card__ribbon--mail {
  color: #0e700e;
}

.captured-group__link {
  display: none;
}

.captured-group__related {
  margin: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}

.captured-group__related-label {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8a8886;
}

.captured-group__related-pos {
  font-size: 0.7rem;
  font-weight: 600;
  color: #605e5c;
  white-space: nowrap;
}

.captured-group__related-stepper {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
  flex-shrink: 0;
}

.captured-group__related-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border: 1px solid #edebe9;
  background: #fff;
  color: #201f1e;
  font-size: 1rem;
  cursor: pointer;
}

.captured-group__related-arrow:hover:not(:disabled) {
  background: #deecf9;
  border-color: #0078d4;
  color: #0078d4;
}

.captured-group__related-arrow:disabled {
  opacity: 0.35;
  cursor: default;
}

.captured-group__related-list {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.captured-group__related-list::-webkit-scrollbar {
  display: none;
}

.captured-group__related-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  max-width: 11.5rem;
  flex: 0 0 auto;
  padding: 0.28rem 0.6rem;
  border: 1px solid transparent;
  background: #fff;
  color: #201f1e;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
}

.captured-group__related-chip--active {
  background: #cfe4fa;
  border-color: #0078d4;
  color: #0078d4;
  box-shadow: inset 3px 0 0 #0078d4;
}

.captured-group__related-chip span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.captured-group__related-chip:hover {
  background: #f3f2f1;
}

.captured-group__related-chip--active:hover {
  background: #cfe4fa;
}

.captured-group__qwen-status,
.captured-group__qwen-hint {
  margin: 0.4rem 0 0;
  font-size: 0.74rem;
  color: #605e5c;
}

.captured-group__qwen-error {
  margin: 0.4rem 0 0;
  font-size: 0.74rem;
  color: #a4262c;
}

.captured-mail-attachments {
  margin: 0.65rem 0 0;
  padding: 0.55rem 0.6rem;
}

.captured-mail-attachments .mail-attachments__title {
  display: flex;
  width: 100%;
}

.captured-mail-attachments__saved {
  margin-left: auto;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #0e700e;
}

.draft-bills-captured__unmatched {
  margin: 0.25rem 0 0;
  font-size: 0.76rem;
  color: #8a8886;
  text-align: center;
}

@media (max-width: 900px) {
  .captured-group__pair {
    grid-template-columns: 1fr;
  }

  .captured-bill-card.dbc-card {
    padding-right: 0;
  }

  .captured-bill-card--mail.dbc-card {
    padding: 1rem 0 0;
    border-left: none;
    border-top: 1px solid #edebe9;
  }
}
</style>
